import { Stack, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import { useEffectiveTenant } from '../../hooks/useEffectiveTenant';

/**
 * De qué plataforma y de qué país es la pantalla que se está mirando.
 *
 * Va al pie del sidebar, al lado de la versión. Nació porque en una misma sesión se pasa
 * de Imperium a un tenant de Booster —el modo soporte lo hace en un click— y no había
 * ninguna marca a la vista de en cuál se está: dos CRMs iguales, con reglas distintas
 * (features del plan, cupo de Gustavo, qué se ve y qué no). El país importa por lo mismo:
 * hoy hay tenants en UY, PY y CL, y el mercado cambia lo que la pantalla significa.
 *
 * ⛔ Muestra la PLATAFORMA, no la inmobiliaria. El nombre de la inmobiliaria ya está
 * arriba, en el branding: acá lo que se responde es "¿esto es Imperium o es Booster?".
 *
 * En modo soporte muestra el tenant EFECTIVO —el que se está mirando— y no el de quien
 * mira, que es justo el caso donde uno se pierde.
 */

/**
 * La bandera de un ISO-2, con los caracteres regionales de Unicode.
 *
 * Sin assets ni librería: 'UY' → 🇺🇾. Devuelve vacío si el código no son dos letras, así
 * un país mal cargado no pinta un cuadrado raro.
 */
export function banderaDe(iso2?: string): string {
  const c = (iso2 ?? '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(c)) return '';
  return String.fromCodePoint(...[...c].map((l) => 0x1f1e6 + l.charCodeAt(0) - 65));
}

/**
 * `compacto` lo pone DENTRO de la ficha del usuario, arriba de todo, en vez de al pie.
 *
 * ⛔ Al pie no se veía. El sidebar scrollea y el sello quedaba en y=800 de 851: con una
 * ventana un poco más baja —o el menú desplegado— caía abajo del corte y había que buscarlo.
 * La ficha del usuario, en cambio, está fija arriba y es justo donde uno ya mira para saber
 * quién es: al lado de "Agente" es donde el dato hace falta.
 */
export function SelloDePlataforma({ collapsed, rolReal, compacto }: { collapsed?: boolean; rolReal?: string; compacto?: boolean }) {
  const theme = useTheme();
  const { tenantMode, tenantCountry, isActing } = useEffectiveTenant();

  /**
   * ⛔ Las cuentas de PLATAFORMA no están en ningún tenant hasta que entran en modo
   * soporte: `/auth/me` no les devuelve ni modo ni país. Sin este caso el sello mostraba
   * "Imperium" —de un `tenantMode` viejo que quedó en el store persistido— y decía algo
   * que no era cierto justo para quien más salta entre inmobiliarias.
   */
  const esPlataforma = rolReal === 'booster_admin' || rolReal === 'booster_support';
  if (esPlataforma && !isActing) {
    const t = 'Cuenta de plataforma: no estás dentro de ninguna inmobiliaria';
    return (
      <Tooltip title={t} placement={collapsed ? 'right' : 'top'}>
        <Typography
          component="div"
          sx={{
            mt: compacto ? 0.4 : 1, textAlign: compacto ? 'left' : 'center', fontSize: 9.5,
            fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.disabled',
          }}
        >
          {collapsed ? '·' : 'Plataforma'}
        </Typography>
      </Tooltip>
    );
  }

  // Sin modo resuelto no se inventa nada.
  if (!tenantMode) return null;

  const esImperium = tenantMode === 'native';
  const plataforma = esImperium ? 'Imperium' : 'Booster';
  const bandera = banderaDe(tenantCountry);
  const pais = (tenantCountry ?? '').toUpperCase();

  const titulo = `${plataforma}${pais ? ` · ${pais}` : ''}${isActing ? ' · estás en modo soporte' : ''}`;

  // Colapsado el sidebar no entra el texto: queda la bandera, que es lo que más rápido se
  // reconoce, y el resto en el tooltip.
  if (collapsed) {
    return (
      <Tooltip title={titulo} placement="right">
        <Typography sx={{ display: 'block', textAlign: 'center', fontSize: 13, lineHeight: 1, mt: 1 }}>
          {bandera || plataforma[0]}
        </Typography>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={titulo} placement="top">
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        justifyContent={compacto ? 'flex-start' : 'center'}
        sx={{ mt: compacto ? 0.4 : 1, flexShrink: 0 }}
      >
        <Typography
          component="span"
          sx={{
            fontSize: 9.5, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase',
            px: 0.75, py: '1px', borderRadius: 1,
            color: theme.palette.primary.main,
            bgcolor: alpha(theme.palette.primary.main, 0.10),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
          }}
        >
          {plataforma}
        </Typography>
        {bandera && (
          <Typography component="span" sx={{ fontSize: 11, lineHeight: 1 }}>
            {bandera}
          </Typography>
        )}
        {pais && (
          <Typography component="span" sx={{ fontSize: 9.5, fontWeight: 700, color: 'text.disabled', letterSpacing: '0.06em' }}>
            {pais}
          </Typography>
        )}
      </Stack>
    </Tooltip>
  );
}
